import { ErrorBoundaryRetry } from "@package/ui-components";
import { useCallback, useEffect, useState } from "react";
import { type ReactPlayerProps } from "react-player";
import { ReactPlayerThrowsOnError } from "../ReactPlayerThrowsOnError/ReactPlayerThrowsOnError";

const STALL_DELAY_MS = 5000;

type ReactPlayerErrorBoundaryRetryProps = ReactPlayerProps & {
  onErrorEvent?: (error: Error) => void;
  onMediaStalled?: () => void;
};

export function ReactPlayerErrorBoundaryRetry({
  onErrorEvent,
  onMediaStalled,
  ...props
}: ReactPlayerErrorBoundaryRetryProps) {
  const [duration, setDuration] = useState<number>(0);
  const [progress, setProgress] = useState<number>(0);

  const onCompleteVideo = useCallback(() => {
    onMediaStalled?.();
    setDuration(0);
    setProgress(0);
  }, [onMediaStalled]);

  useEffect(
    function effectMediaEnsurePlaybackCompleted() {
      let timeoutDuration: ReturnType<typeof setTimeout> | undefined =
        undefined;

      if (!props?.loop && Number.isFinite(duration) && duration > 0) {
        timeoutDuration = setTimeout(
          () => {
            console.error(
              `[ReactPlayer] Media playback did not complete. Stalled after ${duration} seconds.`,
            );
            onCompleteVideo();
          },
          duration * 1000 + STALL_DELAY_MS,
        );
      }

      return () => {
        if (timeoutDuration) {
          clearTimeout(timeoutDuration);
        }
      };
    },
    [duration, onCompleteVideo, props?.loop],
  );

  useEffect(
    function effectMediaEnsureProgress() {
      let timeoutProgress: ReturnType<typeof setTimeout> | undefined =
        undefined;

      if (Number.isFinite(progress) && progress > 0) {
        timeoutProgress = setTimeout(() => {
          console.error(
            `[ReactPlayer] Media playback stalled at ${progress} seconds.`,
          );
          onCompleteVideo();
        }, STALL_DELAY_MS);
      }

      return () => {
        if (timeoutProgress) {
          clearTimeout(timeoutProgress);
        }
      };
    },
    [progress, onCompleteVideo],
  );

  useEffect(
    function effectOnSrcChanged() {
      if (props.url) {
        setDuration(0);
        setProgress(0);
      }
    },
    [props.url],
  );

  return (
    <ErrorBoundaryRetry onError={(error) => onErrorEvent?.(error)}>
      <ReactPlayerThrowsOnError
        {...props}
        onDuration={(duration) => {
          props.onDuration?.(duration);
          setDuration(duration);
        }}
        onEnded={() => {
          props.onEnded?.();
          setDuration(0);
          setProgress(0);
        }}
        onProgress={(progress) => {
          props.onProgress?.(progress);
          setProgress(progress.playedSeconds);
        }}
      />
    </ErrorBoundaryRetry>
  );
}
